import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  addBlock, addChapter, addLesson, deleteBlock, deleteChapter, deleteLesson, getCourse, getCurriculumAdmin,
  listBlocks, saveCourse, swapPositions, updateBlock, updateChapter, updateLesson,
} from '../../lib/adminApi';
import { friendlyError } from '../../lib/supabase';
import type { BlockType, Chapter, CompletionRule, Course, CourseStatus, Lesson, LessonBlock } from '../../lib/types';
import { Badge, Button, Callout, Icon, Spinner, TextField } from '../../components/ui';
import { StatusBadge } from './AdminCourses';

export function AdminCourseEditor() {
  const { courseId = '' } = useParams();
  const [course, setCourse] = useState<Course | null | undefined>(undefined);
  const [tab, setTab] = useState<'curriculum' | 'settings'>('curriculum');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { getCourse(courseId).then(setCourse).catch((e) => setError(friendlyError(e))); }, [courseId]);

  if (error) return <Callout variant="error">{error}</Callout>;
  if (course === undefined) return <Spinner />;
  if (course === null) return <Callout variant="error">Course not found, or you don’t have access to it.</Callout>;

  return (
    <>
      <div className="small muted"><Link to="/admin/courses">Courses</Link> / {course.title}</div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row">
          <h1 style={{ margin: 0, fontSize: 28, lineHeight: '36px', fontWeight: 400, color: 'var(--brand)' }}>{course.title}</h1>
          <StatusBadge status={course.status} />
        </div>
        <Link className="bl-btn bl-btn-secondary bl-btn-sm" to={`/learn/${course.slug}`}><Icon name="play" />Preview as student</Link>
      </div>
      <div role="tablist" aria-label="Course sections" className="row" style={{ gap: 4, borderBottom: '1px solid var(--rule)' }}>
        {(['curriculum', 'settings'] as const).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            style={{ border: 0, background: 'transparent', padding: '10px 14px', font: `${tab === t ? 600 : 400} 14px var(--font-sans)`, color: tab === t ? 'var(--brand)' : 'var(--ink-muted)', borderBottom: `3px solid ${tab === t ? 'var(--brand)' : 'transparent'}`, cursor: 'pointer', marginBottom: -1 }}>
            {t === 'curriculum' ? 'Curriculum' : 'Settings & pricing'}
          </button>
        ))}
      </div>
      {tab === 'settings' ? <CourseSettings course={course} onSaved={setCourse} /> : <Curriculum course={course} />}
    </>
  );
}

function CourseSettings({ course, onSaved }: { course: Course; onSaved: (c: Course) => void }) {
  const [f, setF] = useState({
    title: course.title, slug: course.slug, subtitle: course.subtitle ?? '', description: course.description ?? '',
    objectives: course.objectives.join('\n'), duration: String(course.duration_minutes ?? ''), pdh: String(course.pdh ?? ''),
    price: (course.price_cents / 100).toFixed(2), status: course.status, sequential: course.sequential,
    certificate: course.certificate_enabled, image: course.image_path ?? '', access: String(course.access_days ?? ''),
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    const price = Math.round(Number(f.price) * 100);
    if (!Number.isFinite(price) || price < 0) { setMsg({ ok: false, text: 'Enter a valid price.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const saved = await saveCourse(course.id, {
        title: f.title.trim(), slug: f.slug.trim(), subtitle: f.subtitle.trim() || null, description: f.description.trim() || null,
        objectives: f.objectives.split('\n').map((s) => s.trim()).filter(Boolean),
        duration_minutes: f.duration ? Number(f.duration) : null, pdh: f.pdh ? Number(f.pdh) : null,
        price_cents: price, status: f.status as CourseStatus, sequential: f.sequential, certificate_enabled: f.certificate,
        image_path: f.image.trim() || null, access_days: f.access ? Number(f.access) : null,
      });
      onSaved(saved);
      setMsg({ ok: true, text: 'Saved.' });
    } catch (err) { setMsg({ ok: false, text: friendlyError(err) }); }
    setBusy(false);
  }

  return (
    <form className="stack" onSubmit={submit} style={{ maxWidth: 960 }}>
      {msg && <Callout variant={msg.ok ? 'note' : 'error'} role="status">{msg.text}</Callout>}
      <div className="form-grid">
        <TextField label="Title" required value={f.title} onChange={set('title')} />
        <TextField label="URL slug" help="Lowercase words joined by hyphens." required value={f.slug} onChange={set('slug')} />
        <TextField label="Subtitle" value={f.subtitle} onChange={set('subtitle')} />
        <TextField label="Course image URL" value={f.image} onChange={set('image')} />
      </div>
      <TextField label="Description" multiline rows={4} value={f.description} onChange={set('description')} />
      <TextField label="Learning objectives" help="One per line." multiline rows={4} value={f.objectives} onChange={set('objectives')} />
      <div className="form-grid">
        <TextField label="Price (USD)" inputMode="decimal" value={f.price} onChange={set('price')} />
        <TextField label="Professional development hours" inputMode="decimal" value={f.pdh} onChange={set('pdh')} />
        <TextField label="Duration (minutes)" inputMode="numeric" value={f.duration} onChange={set('duration')} />
        <TextField label="Access length (days)" help="Blank = no expiry." inputMode="numeric" value={f.access} onChange={set('access')} />
        <label className="bl-field"><span className="bl-field-label">Status</span>
          <select className="bl-select" value={f.status} onChange={set('status')}>
            <option value="draft">Draft</option><option value="published">Published</option>
            <option value="private">Private (enrolled students only)</option><option value="archived">Archived</option>
          </select>
        </label>
      </div>
      <label className="check-row"><input type="checkbox" checked={f.sequential} onChange={set('sequential')} /> Sequential: learners complete required lessons in order</label>
      <label className="check-row"><input type="checkbox" checked={f.certificate} onChange={set('certificate')} /> Issue a certificate of completion</label>
      <div><Button type="submit" busy={busy}>Save settings</Button></div>
    </form>
  );
}

function Curriculum({ course }: { course: Course }) {
  const [chapters, setChapters] = useState<Chapter[] | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { const r = await getCurriculumAdmin(course.id); setChapters(r.chapters); setLessons(r.lessons); }
    catch (e) { setError(friendlyError(e)); }
  }, [course.id]);
  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<unknown>) => { setError(null); try { await fn(); await load(); } catch (e) { setError(friendlyError(e)); } };
  if (!chapters) return <Spinner />;
  const sorted = [...chapters].sort((a, b) => a.position - b.position);
  const lessonsOf = (id: string) => lessons.filter((l) => l.chapter_id === id).sort((a, b) => a.position - b.position);
  const nextPos = (xs: { position: number }[]) => (xs.length ? Math.max(...xs.map((x) => x.position)) + 1 : 1);
  const lesson = lessons.find((l) => l.id === selected) ?? null;

  return (
    <div className="two-col" style={{ alignItems: 'flex-start' }}>
      <section className="main" aria-label="Curriculum outline">
        {error && <Callout variant="error">{error}</Callout>}
        <p className="small muted" style={{ margin: 0 }}>{sorted.length} chapters · {lessons.length} lessons · changes save immediately</p>
        {sorted.map((ch, i) => (
          <div key={ch.id} className="box" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="row" style={{ padding: '10px 12px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--rule)' }}>
              <InlineTitle value={ch.title} label="Chapter title" onSave={(t) => run(() => updateChapter(ch.id, { title: t }))} strong />
              <IconBtn label="Move chapter up" disabled={i === 0} onClick={() => run(() => swapPositions('chapters', ch, sorted[i - 1]!))}>↑</IconBtn>
              <IconBtn label="Move chapter down" disabled={i === sorted.length - 1} onClick={() => run(() => swapPositions('chapters', ch, sorted[i + 1]!))}>↓</IconBtn>
              <IconBtn label="Delete chapter" danger onClick={() => { if (confirm(`Delete “${ch.title}” and its lessons?`)) void run(() => deleteChapter(ch.id)); }}>✕</IconBtn>
            </div>
            {lessonsOf(ch.id).map((l, j, arr) => (
              <div key={l.id} className="row" style={{ padding: '8px 12px', borderBottom: '1px solid var(--rule)', background: selected === l.id ? 'var(--tint-blue)' : undefined }}>
                <button type="button" onClick={() => setSelected(l.id)} style={{ flex: '1 1 200px', textAlign: 'left', border: 0, background: 'transparent', font: 'inherit', color: 'var(--ink)', cursor: 'pointer', padding: '4px 0' }}>
                  {l.title}
                </button>
                {l.is_preview && <Badge tone="brand">Preview</Badge>}
                {!l.is_required && <Badge>Optional</Badge>}
                <IconBtn label="Move lesson up" disabled={j === 0} onClick={() => run(() => swapPositions('lessons', l, arr[j - 1]!))}>↑</IconBtn>
                <IconBtn label="Move lesson down" disabled={j === arr.length - 1} onClick={() => run(() => swapPositions('lessons', l, arr[j + 1]!))}>↓</IconBtn>
              </div>
            ))}
            <div style={{ padding: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => run(async () => { const l = await addLesson(ch.id, 'New lesson', nextPos(lessonsOf(ch.id))); setSelected(l.id); })}>+ Add lesson</Button>
            </div>
          </div>
        ))}
        <div><Button variant="secondary" onClick={() => run(() => addChapter(course.id, `Chapter ${sorted.length + 1}`, nextPos(sorted)))}>+ Add chapter</Button></div>
      </section>
      <section className="side" style={{ flexBasis: 420 }} aria-label="Lesson editor">
        {lesson ? <LessonEditor key={lesson.id} lesson={lesson} siblings={lessons} onChanged={load} onDeleted={() => { setSelected(null); void load(); }} />
          : <div className="empty">Select a lesson to edit its settings and content.</div>}
      </section>
    </div>
  );
}

function LessonEditor({ lesson, siblings, onChanged, onDeleted }: { lesson: Lesson; siblings: Lesson[]; onChanged: () => void; onDeleted: () => void }) {
  const [blocks, setBlocks] = useState<LessonBlock[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => listBlocks(lesson.id).then(setBlocks).catch((e) => setError(friendlyError(e))), [lesson.id]);
  useEffect(() => { void load(); }, [load]);
  const run = async (fn: () => Promise<unknown>, reloadLesson = false) => {
    setError(null);
    try { await fn(); await load(); if (reloadLesson) onChanged(); } catch (e) { setError(friendlyError(e)); }
  };
  const patch = (p: Partial<Lesson>) => run(() => updateLesson(lesson.id, p), true);
  const sortedBlocks = [...(blocks ?? [])].sort((a, b) => a.position - b.position);
  const nextPos = sortedBlocks.length ? sortedBlocks[sortedBlocks.length - 1]!.position + 1 : 1;
  const ADDABLE: { type: BlockType; label: string; config: Record<string, unknown> }[] = [
    { type: 'text', label: 'Text', config: { markdown: '' } },
    { type: 'video', label: 'Video', config: { src: '' } },
    { type: 'image', label: 'Image', config: { src: '', alt: '' } },
    { type: 'pdf', label: 'PDF / download', config: { label: '', url: '' } },
    { type: 'embed', label: 'Embed', config: { url: '', title: '' } },
  ];

  return (
    <div className="box stack">
      {error && <Callout variant="error">{error}</Callout>}
      <InlineTitle value={lesson.title} label="Lesson title" onSave={(t) => patch({ title: t })} strong />
      <div className="form-grid">
        <label className="bl-field"><span className="bl-field-label">Completion rule</span>
          <select className="bl-select" value={lesson.completion_rule} onChange={(e) => patch({ completion_rule: e.target.value as CompletionRule })}>
            <option value="manual">Mark complete manually</option>
            <option value="video_pct">Watch part of the video</option>
            <option value="activity_passed">Pass the lesson’s activity</option>
          </select>
        </label>
        {lesson.completion_rule === 'video_pct' && (
          <TextField label="Viewing threshold (%)" inputMode="numeric" defaultValue={lesson.video_threshold_pct}
            onBlur={(e) => { const v = Number(e.target.value); if (v >= 1 && v <= 100 && v !== lesson.video_threshold_pct) void patch({ video_threshold_pct: v }); }} />
        )}
        <TextField label="Estimated minutes" inputMode="numeric" defaultValue={lesson.est_minutes ?? ''}
          onBlur={(e) => { const v = e.target.value ? Number(e.target.value) : null; if (v !== lesson.est_minutes) void patch({ est_minutes: v }); }} />
        <label className="bl-field"><span className="bl-field-label">Prerequisite</span>
          <select className="bl-select" value={lesson.prerequisite_lesson_id ?? ''} onChange={(e) => patch({ prerequisite_lesson_id: e.target.value || null })}>
            <option value="">None</option>
            {siblings.filter((s) => s.id !== lesson.id).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </label>
      </div>
      <label className="check-row"><input type="checkbox" checked={lesson.is_preview} onChange={(e) => patch({ is_preview: e.target.checked })} /> Free preview (visible before enrolling)</label>
      <label className="check-row"><input type="checkbox" checked={lesson.is_required} onChange={(e) => patch({ is_required: e.target.checked })} /> Required for completion</label>

      <h3 style={{ margin: '8px 0 0', fontSize: 16, color: 'var(--ink-strong)' }}>Content blocks</h3>
      {!blocks ? <Spinner /> : sortedBlocks.map((b, i) => (
        <div key={b.id} className="stack" style={{ gap: 8, border: '1px solid var(--rule-strong)', borderRadius: 'var(--radius-sm)', padding: 12 }}>
          <div className="row" style={{ gap: 6 }}>
            <strong style={{ flexGrow: 1, fontSize: 14, textTransform: 'capitalize' }}>{b.type}</strong>
            <IconBtn label="Move block up" disabled={i === 0} onClick={() => run(() => swapPositions('lesson_blocks', b, sortedBlocks[i - 1]!))}>↑</IconBtn>
            <IconBtn label="Move block down" disabled={i === sortedBlocks.length - 1} onClick={() => run(() => swapPositions('lesson_blocks', b, sortedBlocks[i + 1]!))}>↓</IconBtn>
            <IconBtn label="Delete block" danger onClick={() => { if (confirm('Delete this block?')) void run(() => deleteBlock(b.id)); }}>✕</IconBtn>
          </div>
          <BlockFields block={b} onSave={(config) => run(() => updateBlock(b.id, { config }))} />
        </div>
      ))}
      <div className="row" style={{ gap: 6 }}>
        <span className="small muted">Add:</span>
        {ADDABLE.map((a) => <Button key={a.type} size="sm" variant="secondary" onClick={() => run(() => addBlock(lesson.id, a.type, nextPos, a.config))}>{a.label}</Button>)}
      </div>
      <p className="small muted" style={{ margin: 0 }}>Interactive activities are added from the activity builder (next release).</p>
      <div><Button variant="danger" size="sm" onClick={() => { if (confirm(`Delete lesson “${lesson.title}”? Learner progress for it is removed.`)) void deleteLesson(lesson.id).then(onDeleted).catch((e) => setError(friendlyError(e))); }}>Delete lesson</Button></div>
    </div>
  );
}

function BlockFields({ block, onSave }: { block: LessonBlock; onSave: (config: Record<string, unknown>) => void }) {
  const [cfg, setCfg] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(block.config).map(([k, v]) => [k, String(v ?? '')])));
  const dirty = JSON.stringify(cfg) !== JSON.stringify(Object.fromEntries(Object.entries(block.config).map(([k, v]) => [k, String(v ?? '')])));
  const field = (k: string, label: string, multiline = false, help?: string) => multiline
    ? <TextField label={label} help={help} multiline rows={6} value={cfg[k] ?? ''} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} />
    : <TextField label={label} help={help} value={cfg[k] ?? ''} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} />;
  return (
    <>
      {block.type === 'text' && field('markdown', 'Text', true, 'Supports ## headings, **bold**, *italic*, - lists and [links](https://…).')}
      {block.type === 'video' && <>{field('src', 'Video URL', false, 'A direct video file URL for now; protected streaming arrives with Cloudflare Stream.')}{field('captions', 'Captions file URL (.vtt)')}</>}
      {block.type === 'image' && <>{field('src', 'Image URL')}{field('alt', 'Alt text (describe the image)')}{field('caption', 'Caption')}</>}
      {(block.type === 'pdf' || block.type === 'file' || block.type === 'slides') && <>{field('label', 'Link label')}{field('url', 'File URL')}</>}
      {block.type === 'embed' && <>{field('url', 'Embed URL', false, 'YouTube (nocookie), Vimeo, ArcGIS or Google Docs.')}{field('title', 'Accessible title')}</>}
      {!['text', 'video', 'image', 'pdf', 'file', 'slides', 'embed'].includes(block.type) && <p className="small muted" style={{ margin: 0 }}>Edited in its own builder.</p>}
      {dirty && <div><Button size="sm" onClick={() => onSave(cfg)}>Save block</Button></div>}
    </>
  );
}

function InlineTitle({ value, label, onSave, strong }: { value: string; label: string; onSave: (v: string) => void; strong?: boolean }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input aria-label={label} value={v} onChange={(e) => setV(e.target.value)}
      onBlur={() => { const t = v.trim(); if (t && t !== value) onSave(t); else setV(value); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      style={{ flex: '1 1 200px', minWidth: 0, border: '1px solid transparent', borderRadius: 3, padding: '6px 8px', background: 'transparent', color: 'var(--ink-strong)', font: `${strong ? 600 : 400} 15px var(--font-sans)` }}
      onFocus={(e) => { e.target.style.borderColor = 'var(--rule-strong)'; }} />
  );
}

function IconBtn({ label, onClick, disabled, danger, children }: { label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      style={{ width: 32, height: 32, border: '1px solid var(--rule)', borderRadius: 3, background: 'var(--surface)', color: danger ? 'var(--danger)' : 'var(--ink)', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1 }}>
      {children}
    </button>
  );
}
